import React, { useEffect, useRef, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { isEmpty } from 'lodash'

import { ResetTemporaryPassword } from 'components'
import { TopBar, CardCompanies, CardOrganizations } from './components'
import { ScreenLoading } from 'layouts/Main/components'
import { Box, Backdrop, CircularProgress } from '@material-ui/core'

import useAuth from 'hooks/useAuth'
import useSnackbar from 'hooks/useSnackbar'
import useFetch from 'hooks/useFetch'
import useInfiniteScroll from 'hooks/useInfiniteScroll'
import useClientPortalTheme from 'hooks/useClientPortalTheme'

import * as service from 'service'
import constants from 'constants/index'
import styles from './styles'
import { ReactComponent as HexagonDetail } from 'images/hexagon-background-auth.svg'
import LogoWhite from 'images/logo-dponet-white.svg'

const SelectCompany = () => {
  const auth = useAuth()
  const isAdvisor =
    auth.user?.kind === 'advisor' || auth.user?.organizations?.length > 0

  const [isSelectingCompany, setIsSelectingCompany] = useState(false)
  const [companySearch, setCompanySearch] = useState('')
  const [debouncedCompanySearch, setDebouncedCompanySearch] = useState('')
  const [organizationSearch, setOrganizationSearch] = useState('')
  const [debouncedOrgSearch, setDebouncedOrgSearch] = useState('')
  const snackbar = useSnackbar()
  const { clientPortalTheme } = useClientPortalTheme()
  const classes = styles(clientPortalTheme?.secondaryColor)()

  const {
    page: companyPage,
    allItems: usersCompanies,
    appendItems: appendCompanies,
    loadMore: loadMoreCompanies,
    reset: resetCompanies,
    hasMore: hasMoreCompanies,
  } = useInfiniteScroll(5)

  const {
    page: organizationPage,
    allItems: allOrganizations,
    appendItems: appendOrganizations,
    loadMore: loadMoreOrganizations,
    reset: resetOrganizations,
  } = useInfiniteScroll(5)

  const noCompaniesSnackbar = () => {
    snackbar.open({
      message:
        'Você não possui empresas ativas ou vínculos pendentes ou aprovados com clientes DPOnet',
      variant: 'error',
    })
    auth.loadOut(true)
    service.dponet.auth.logout()
  }

  const userCompanyFilter = (userCompany, filterDpo = false) => {
    if (
      filterDpo &&
      userCompany?.profile?.id === constants.profile.OUTSOURCED_DPO_PROFILE_ID
    ) {
      return false
    }

    if (userCompany?.company === null) {
      return false
    }

    return true
  }

  const isFirstCompanyRender = useRef(true)
  const isFirstOrgRender = useRef(true)
  const initialCompanyLoadDone = useRef(false)

  useEffect(() => {
    if (isFirstCompanyRender.current) {
      isFirstCompanyRender.current = false
      return
    }
    const timer = setTimeout(() => {
      unstable_batchedUpdates(() => {
        setDebouncedCompanySearch(companySearch)
        resetCompanies()
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [companySearch])

  useEffect(() => {
    if (isFirstOrgRender.current) {
      isFirstOrgRender.current = false
      return
    }
    const timer = setTimeout(() => {
      unstable_batchedUpdates(() => {
        setDebouncedOrgSearch(organizationSearch)
        resetOrganizations()
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [organizationSearch])

  const { response, isLoading } = useFetch(
    service.dponet.usersCompanies.list,
    {
      status: constants.userCompanies.ACTIVE,
      perPage: 5,
      minimal: true,
      page: companyPage,
      companyName: debouncedCompanySearch || undefined,
    },
    [companyPage, debouncedCompanySearch],
  )

  const { response: organizationsResponse, isLoading: isLoadingOrganizations } =
    useFetch(
      service.dponet.organizations.list,
      {
        page: organizationPage,
        perPage: 5,
        name: debouncedOrgSearch || undefined,
        favorites: true,
      },
      [organizationPage, debouncedOrgSearch],
      isAdvisor,
    )

  const userCompanies = response?.data?.userCompanies

  useEffect(() => {
    const newOrgs = organizationsResponse?.data?.organizations
    if (!newOrgs) return

    appendOrganizations(newOrgs)
    // eslint-disable-next-line
  }, [organizationsResponse])

  useEffect(() => {
    if (userCompanies === undefined || userCompanies === null) return
    initialCompanyLoadDone.current = true
    appendCompanies(userCompanies)
  }, [userCompanies])

  useEffect(() => {
    if (hasMoreCompanies) return
    if (debouncedCompanySearch) return
    const validCompanies = usersCompanies.filter((userCompany) =>
      userCompanyFilter(userCompany, true),
    )
    if (isEmpty(validCompanies)) noCompaniesSnackbar()
  }, [hasMoreCompanies])

  if (auth.temporaryPassword) {
    return <ResetTemporaryPassword />
  }

  const handleLoadMoreCompanies = () => {
    if (!isLoading) loadMoreCompanies()
  }

  const handleLoadMoreOrgs = () => {
    if (!isLoadingOrganizations) loadMoreOrganizations()
  }

  return (
    <>
      <Backdrop
        open={isSelectingCompany && auth.loaded}
        style={{ zIndex: 9999 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <ScreenLoading
        loaded={
          auth.loaded &&
          (initialCompanyLoadDone.current ||
            !(companyPage === 1 && isLoading && usersCompanies.length === 0))
        }
      >
        <TopBar className={classes.topBar} />
        {!isAdvisor && (
          <img
            src={clientPortalTheme?.logo || LogoWhite}
            alt="Logo"
            className={classes.logo}
          />
        )}
        <HexagonDetail className={classes.detailBottomRight} />
        <HexagonDetail className={classes.detailTopLeft} />
        <Box className={classes.content}>
          <CardCompanies
            usersCompanies={usersCompanies.filter((userCompany) =>
              userCompanyFilter(userCompany, true),
            )}
            isLoading={isLoading}
            onLoadMore={handleLoadMoreCompanies}
            onBeforeSelect={() => setIsSelectingCompany(true)}
            searchInput={companySearch}
            onSearch={setCompanySearch}
          />
          {isAdvisor && (
            <CardOrganizations
              usersOrganizations={allOrganizations}
              isLoading={isLoadingOrganizations}
              onLoadMore={handleLoadMoreOrgs}
              searchInput={organizationSearch}
              onSearch={setOrganizationSearch}
            />
          )}
        </Box>
      </ScreenLoading>
    </>
  )
}

export default SelectCompany
